import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatNum } from "../utils/constants";

/** jsPDF helvetica Türkçe karakterleri bozduğu için sadeleştirme */
function t(value) {
  return String(value ?? "")
    .replace(/ı/g, "i")
    .replace(/İ/g, "I")
    .replace(/ş/g, "s")
    .replace(/Ş/g, "S")
    .replace(/ğ/g, "g")
    .replace(/Ğ/g, "G")
    .replace(/ü/g, "u")
    .replace(/Ü/g, "U")
    .replace(/ö/g, "o")
    .replace(/Ö/g, "O")
    .replace(/ç/g, "c")
    .replace(/Ç/g, "C");
}

const COLORS = {
  dirty: [44, 62, 80],
  clean: [52, 152, 219],
  prod: [243, 156, 18],
  header: [15, 23, 42],
  muted: [100, 116, 139],
  line: [226, 232, 240],
  card: [248, 250, 252],
};

function ensureSpace(doc, y, need, margin = 40) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + need > pageHeight - margin) {
    doc.addPage();
    return 36;
  }
  return y;
}

function drawLegend(doc, x, y, items) {
  let cx = x;
  items.forEach((item) => {
    doc.setFillColor(...item.color);
    doc.roundedRect(cx, y - 3, 8, 8, 1, 1, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.muted);
    doc.text(t(item.label), cx + 11, y + 3);
    cx += 11 + doc.getTextWidth(t(item.label)) + 12;
  });
}

function drawGroupedBars(doc, { x, y, width, height, data, keys, colors, maxValue }) {
  const n = Math.max(data.length, 1);
  const groupGap = 10;
  const groupWidth = (width - groupGap * (n - 1)) / n;
  const barGap = 2;
  const barWidth = (groupWidth - barGap * (keys.length - 1)) / keys.length;
  const maxV = Math.max(maxValue || 0, 1);

  doc.setDrawColor(...COLORS.line);
  doc.setLineWidth(0.4);
  doc.line(x, y + height, x + width, y + height);

  data.forEach((row, i) => {
    const gx = x + i * (groupWidth + groupGap);
    keys.forEach((key, ki) => {
      const value = Number(row[key]) || 0;
      const h = (value / maxV) * (height - 18);
      const bx = gx + ki * (barWidth + barGap);
      const by = y + height - h;
      doc.setFillColor(...colors[ki]);
      doc.roundedRect(bx, by, barWidth, Math.max(h, 0.5), 1, 1, "F");
    });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.muted);
    const label = t(row.name || "");
    const lw = doc.getTextWidth(label);
    doc.text(label, gx + (groupWidth - lw) / 2, y + height + 10);
  });
}

function drawHorizontalBars(doc, { x, y, width, rowHeight, data, keys, colors, maxValue }) {
  const maxV = Math.max(maxValue || 0, 1);
  const labelW = 78;
  const barArea = width - labelW - 8;
  let cy = y;

  data.forEach((row) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(30, 41, 59);
    doc.text(t(row.name || ""), x, cy + 8);

    const barH = 5;
    keys.forEach((key, ki) => {
      const value = Number(row[key]) || 0;
      const w = (value / maxV) * barArea;
      const by = cy + ki * (barH + 2);
      doc.setFillColor(...colors[ki]);
      doc.roundedRect(x + labelW, by, Math.max(w, 0.5), barH, 1, 1, "F");
    });
    cy += rowHeight;
  });
  return cy;
}

/**
 * Çamaşırhane panosu PDF raporu — genel toplam + otel bazlı istatistikler.
 */
export function downloadDashboardPdfReport({
  periodLabel,
  year,
  months,
  grand,
  efficiency,
  hotelChart,
  hotels,
  deptChart,
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 36;
  const contentW = pageWidth - marginX * 2;
  const hotelList = (hotels?.length ? hotels : hotelChart) || [];

  // Header
  doc.setFillColor(...COLORS.header);
  doc.rect(0, 0, pageWidth, 64, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(t("Camasirhane Panosu — PDF Raporu"), marginX, 28);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(t(`Donem: ${periodLabel}`), marginX, 46);
  doc.text(
    t(`Olusturma: ${new Date().toLocaleString("tr-TR")}`),
    pageWidth - marginX - 150,
    46
  );

  let y = 84;

  // —— Genel toplam KPI ——
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(t("Genel Toplam"), marginX, y);
  y += 12;

  const drawKpiGrid = (items, startY) => {
    const cols = 3;
    const gap = 8;
    const cardW = (contentW - gap * (cols - 1)) / cols;
    const cardH = 48;
    items.forEach((kpi, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = marginX + col * (cardW + gap);
      const cy = startY + row * (cardH + gap);
      doc.setFillColor(...COLORS.card);
      doc.setDrawColor(...COLORS.line);
      doc.roundedRect(cx, cy, cardW, cardH, 6, 6, "FD");
      doc.setFillColor(...kpi.color);
      doc.rect(cx, cy, 4, cardH, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.muted);
      doc.text(t(kpi.label.toUpperCase()), cx + 12, cy + 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text(t(kpi.value), cx + 12, cy + 34);
    });
    return startY + Math.ceil(items.length / cols) * (cardH + gap) + 10;
  };

  const grandKpis = [
    { label: "Toplam Kirli", value: `${formatNum(grand.dirty_kg, 0)} kg`, color: COLORS.dirty },
    { label: "Toplam Temiz", value: `${formatNum(grand.clean_kg, 0)} kg`, color: COLORS.clean },
    { label: "Toplam Uretim", value: `${formatNum(grand.production_kg, 0)} kg`, color: COLORS.prod },
    { label: "Uretim / Temiz Verimi", value: `%${formatNum(efficiency, 1)}`, color: [39, 174, 96] },
    { label: "Kirli - Temiz Fark", value: `${formatNum(grand.dirty_clean_diff_kg, 0)} kg`, color: COLORS.dirty },
    { label: "Temiz - Uretim Fark", value: `${formatNum(grand.clean_production_diff_kg, 0)} kg`, color: COLORS.prod },
  ];
  y = drawKpiGrid(grandKpis, y);

  // —— Otel karsilastirma grafigi ——
  y = ensureSpace(doc, y, 200);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(t("Otel Bazinda Karsilastirma"), marginX, y);
  y += 8;
  drawLegend(doc, marginX, y + 8, [
    { label: "Kirli", color: COLORS.dirty },
    { label: "Temiz", color: COLORS.clean },
    { label: "Uretim", color: COLORS.prod },
  ]);
  y += 20;

  const hotelMax = Math.max(
    1,
    ...hotelList.flatMap((h) => [h.Kirli || 0, h.Temiz || 0, h["Üretim"] || 0])
  );
  const hotelChartH = 130;
  drawGroupedBars(doc, {
    x: marginX,
    y,
    width: contentW,
    height: hotelChartH,
    data: hotelList.map((h) => ({
      name: h.name,
      Kirli: h.Kirli,
      Temiz: h.Temiz,
      Uretim: h["Üretim"],
    })),
    keys: ["Kirli", "Temiz", "Uretim"],
    colors: [COLORS.dirty, COLORS.clean, COLORS.prod],
    maxValue: hotelMax,
  });
  y += hotelChartH + 28;

  // —— Otel ozet tablosu ——
  y = ensureSpace(doc, y, 80);
  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    head: [[
      t("Otel"),
      t("Kirli (kg)"),
      t("Temiz (kg)"),
      t("Uretim (kg)"),
      t("Verim %"),
      t("Kirli-Temiz"),
      t("Temiz-Uretim"),
    ]],
    body: [
      ...hotelList.map((h) => [
        t(h.name),
        formatNum(h.Kirli, 1),
        formatNum(h.Temiz, 1),
        formatNum(h["Üretim"], 1),
        formatNum(h.efficiency ?? 0, 1),
        formatNum(h.dirty_clean_diff_kg ?? (h.Kirli - h.Temiz), 1),
        formatNum(h.clean_production_diff_kg ?? (h.Temiz - h["Üretim"]), 1),
      ]),
      [
        t("Genel Toplam"),
        formatNum(grand.dirty_kg, 1),
        formatNum(grand.clean_kg, 1),
        formatNum(grand.production_kg, 1),
        formatNum(efficiency, 1),
        formatNum(grand.dirty_clean_diff_kg, 1),
        formatNum(grand.clean_production_diff_kg, 1),
      ],
    ],
    styles: { fontSize: 7, cellPadding: 3 },
    headStyles: { fillColor: COLORS.header, textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === hotelList.length) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [226, 232, 240];
      }
    },
  });
  y = (doc.lastAutoTable?.finalY || y) + 16;

  // —— Her otel icin ayri istatistik sayfasi ——
  hotelList.forEach((hotel) => {
    if (!(hotel.Kirli || hotel.Temiz || hotel["Üretim"])) return;

    doc.addPage();
    y = 36;

    doc.setFillColor(...COLORS.header);
    doc.roundedRect(marginX, y, contentW, 28, 4, 4, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(t(`Otel: ${hotel.name}`), marginX + 12, y + 18);
    y += 42;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(t("Otel Istatistikleri"), marginX, y);
    y += 10;

    const hotelKpis = [
      { label: "Kirli", value: `${formatNum(hotel.Kirli, 0)} kg`, color: COLORS.dirty },
      { label: "Temiz", value: `${formatNum(hotel.Temiz, 0)} kg`, color: COLORS.clean },
      { label: "Uretim", value: `${formatNum(hotel["Üretim"], 0)} kg`, color: COLORS.prod },
      { label: "Verim", value: `%${formatNum(hotel.efficiency ?? 0, 1)}`, color: [39, 174, 96] },
      { label: "Kirli - Temiz", value: `${formatNum(hotel.dirty_clean_diff_kg ?? 0, 0)} kg`, color: COLORS.dirty },
      { label: "Temiz - Uretim", value: `${formatNum(hotel.clean_production_diff_kg ?? 0, 0)} kg`, color: COLORS.prod },
    ];
    y = drawKpiGrid(hotelKpis, y);

    const depts = hotel.departments || [];
    if (depts.length) {
      y = ensureSpace(doc, y, 60);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text(t("Departman Detayi"), marginX, y);
      y += 8;

      const deptMax = Math.max(1, ...depts.flatMap((d) => [d.Kirli || 0, d.Temiz || 0]));
      const chartH = Math.min(220, Math.max(60, depts.length * 16));
      y = ensureSpace(doc, y, chartH + 30);
      drawLegend(doc, marginX, y + 6, [
        { label: "Kirli", color: COLORS.dirty },
        { label: "Temiz", color: COLORS.clean },
      ]);
      y += 14;
      y = drawHorizontalBars(doc, {
        x: marginX,
        y,
        width: contentW,
        rowHeight: 16,
        data: depts.slice(0, 14),
        keys: ["Kirli", "Temiz"],
        colors: [COLORS.dirty, COLORS.clean],
        maxValue: deptMax,
      });
      y += 12;

      autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [[t("Departman"), t("Kirli (kg)"), t("Temiz (kg)"), t("Kirli - Temiz"), t("Pay %")]],
        body: [
          ...depts.map((d) => {
            const share = hotel.Kirli ? (d.Kirli / hotel.Kirli) * 100 : 0;
            return [
              t(d.name),
              formatNum(d.Kirli, 1),
              formatNum(d.Temiz, 1),
              formatNum(d.Fark, 1),
              formatNum(share, 1),
            ];
          }),
          [
            t("Otel Toplami"),
            formatNum(hotel.Kirli, 1),
            formatNum(hotel.Temiz, 1),
            formatNum(hotel.dirty_clean_diff_kg ?? (hotel.Kirli - hotel.Temiz), 1),
            "100,0",
          ],
        ],
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: COLORS.header, textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didParseCell: (data) => {
          if (data.section === "body" && data.row.index === depts.length) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [226, 232, 240];
          }
        },
      });
    }
  });

  // —— Genel departman ozeti (tum oteller) ——
  doc.addPage();
  y = 36;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.text(t("Tum Oteller — Departman Ozeti"), marginX, y);
  y += 14;

  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    head: [[t("Departman"), t("Kirli (kg)"), t("Temiz (kg)"), t("Kirli - Temiz"), t("Pay %")]],
    body: [
      ...(deptChart || []).map((d) => {
        const share = grand.dirty_kg ? (d.Kirli / grand.dirty_kg) * 100 : 0;
        return [
          t(d.name),
          formatNum(d.Kirli, 1),
          formatNum(d.Temiz, 1),
          formatNum(d.Fark, 1),
          formatNum(share, 1),
        ];
      }),
      [
        t("Genel Toplam"),
        formatNum(grand.dirty_kg, 1),
        formatNum(grand.clean_kg, 1),
        formatNum(grand.dirty_clean_diff_kg, 1),
        "100,0",
      ],
    ],
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: COLORS.header, textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === (deptChart || []).length) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [226, 232, 240];
      }
    },
  });

  const monthTag = (months || []).join("-") || "donem";
  doc.save(`camasirhane_pano_${year}_${monthTag}.pdf`);
}
